// Floating support/troubleshooting dock — replaces the old AiChatDock (which
// wrapped the general VisuN AI chat) in the same bottom-right shortcut spot.
// VisuN AI itself is untouched and still reachable from its own sidebar item;
// this is a separate, purpose-built helper: a scripted decision-tree over the
// most common real issues in this app, with a clear escalation to a human
// (Chat/Call/Email — the same channels Guide → Support already offers) for
// anything it can't resolve. No AI model involved, so it's safe to show to
// every signed-in role rather than gating it like the AI dock was.
import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  LifeBuoy, X, ChevronLeft, MessageCircle, Phone, Mail, ChevronRight,
} from "lucide-react";
import { getToken, getRole } from "../../utils/authService";
import { SUPPORT_EMAIL, SUPPORT_WHATSAPP } from "../../utils/supportContacts";
import "./supportBot.css";

// Grounded in this app's actual behavior (license approval gating on
// /api/agents, the 30s heartbeat/away/offline status model, single-session
// screen viewing, plan-gated monitoring features, update-requires-online) —
// not generic placeholder advice.
const TOPICS = [
  {
    id: "offline",
    q: "My agent shows offline",
    a: [
      "Check the agent process is actually running on that Windows machine — it needs to stay running to report in.",
      "Confirm the device has network access to reach this server.",
      "If it was just installed, double-check the enrollment key and server URL in its .env match this tenant's — a mismatch means it will never register.",
      "Once it reconnects, use the Refresh button on the Devices page to pull the latest status.",
    ],
  },
  {
    id: "not-listed",
    q: "A device isn't showing up in Devices",
    a: [
      "New devices need to be approved before they're listed — open License Manager and check the \"Requested\" tab.",
      "If you're at your seat limit, either free a seat (block an unused device) or add seats from Plans & Billing before approving more.",
    ],
  },
  {
    id: "screen",
    q: "Screen viewing won't connect",
    a: [
      "The device must show Online (not Away or Offline) in Devices — screen viewing needs a live connection.",
      "Only one admin can view a device's screen at a time. If someone else already has it open, wait for them to close it or ask them to.",
      "If it's stuck on \"Connecting…\", it's usually a network/firewall blocking the video connection on that device's network.",
    ],
  },
  {
    id: "update",
    q: "An agent update won't install",
    a: [
      "The device has to be Online for an update to run — if it's Away or Offline, the update can't be pushed until it reconnects.",
      "Once it's back online, use the update icon on that device's row (or \"Update\" in the page header to update every outdated online device at once).",
    ],
  },
  {
    id: "monitoring-empty",
    q: "USB / File / URL monitoring shows no data",
    a: [
      "These are plan-gated features — check Plans & Billing to confirm your current plan includes the monitoring you're expecting.",
      "If it's included and still empty, give a freshly-installed agent a few minutes of activity on the device before expecting data to appear.",
    ],
  },
  {
    id: "login",
    q: "Trouble logging in",
    a: [
      "Double check the email and password, or use \"Continue with...\" if your organization uses single sign-on.",
      "If your account was recently created or disabled, check with your tenant admin.",
      "If your organization's subscription has expired, logins for non-admin roles may be restricted until it's renewed — your admin can check this under Plans & Billing.",
    ],
  },
];

export default function SupportBot() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [activeTopic, setActiveTopic] = useState(null);
  // Tenant-admin only. Role is populated by an async profile sync a moment
  // after login (see ProtectedLayout's syncSession), and this component sits
  // outside ProtectedLayout so it never re-renders when that sync lands — a
  // one-shot getRole() at mount could read the pre-sync empty value and hide
  // the bot for an already-authenticated tenant admin. Polling briefly here
  // catches the value once the sync completes instead.
  const [role, setRole] = useState(() => getRole());

  useEffect(() => {
    const interval = setInterval(() => {
      const current = getRole();
      setRole((prev) => (prev === current ? prev : current));
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Still hidden for logged-out visitors, since every topic here assumes an
  // actual account with devices/agents/billing to troubleshoot.
  if (!getToken()) return null;
  if (role !== "client") return null;
  // Never on the public landing page, even for a tenant-admin who's signed
  // in in this browser but happens to be viewing "/" (App.jsx mounts this
  // outside <Routes> so it isn't naturally scoped away from that route).
  if (location.pathname === "/") return null;

  const topic = TOPICS.find((t) => t.id === activeTopic) || null;

  const reset = () => setActiveTopic(null);

  return (
    <div className="sb-dock">
      {open && (
        <div className="sb-panel">
          <div className="sb-header">
            {topic ? (
              <button className="sb-back" onClick={reset} aria-label="Back to topics">
                <ChevronLeft size={16} />
              </button>
            ) : (
              <span className="sb-mark"><LifeBuoy size={14} /></span>
            )}
            <div className="sb-title">
              <span className="sb-name">Ally</span>
              <span className="sb-sub">GCA Support Assistant</span>
            </div>
            <button className="sb-close" onClick={() => setOpen(false)} aria-label="Close">
              <X size={15} />
            </button>
          </div>

          <div className="sb-body">
            {!topic ? (
              <>
                <p className="sb-greeting">
                  Hi, I'm Ally 👋 Pick what you're running into and I'll walk you through it.
                </p>
                <div className="sb-topics">
                  {TOPICS.map((t) => (
                    <button key={t.id} className="sb-topic" onClick={() => setActiveTopic(t.id)}>
                      {t.q}
                      <ChevronRight size={14} />
                    </button>
                  ))}
                </div>
              </>
            ) : (
              <div className="sb-answer">
                <h4>{topic.q}</h4>
                <ol>
                  {topic.a.map((step, i) => <li key={i}>{step}</li>)}
                </ol>
              </div>
            )}

            <div className="sb-escalate">
              <p>Still stuck? Reach a real person:</p>
              <div className="sb-escalate-row">
                <a href={`https://wa.me/${SUPPORT_WHATSAPP}`} target="_blank" rel="noopener noreferrer" className="sb-escalate-btn">
                  <MessageCircle size={13} /> Chat
                </a>
                <a href={`tel:+${SUPPORT_WHATSAPP}`} className="sb-escalate-btn">
                  <Phone size={13} /> Call
                </a>
                <a href={`mailto:${SUPPORT_EMAIL}`} className="sb-escalate-btn">
                  <Mail size={13} /> Email
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="sb-fab-wrap">
        {!open && (
          <div className="sb-preview" role="tooltip">
            Need help? Chat with Ally 👋
          </div>
        )}
        <button
          className={`sb-fab ${open ? "open" : ""}`}
          onClick={() => setOpen((v) => !v)}
          aria-label={open ? "Close support chat" : "Open support chat"}
          title="Ally — Support"
        >
          {open ? <X size={18} /> : <LifeBuoy size={18} />}
        </button>
      </div>
    </div>
  );
}
